import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {globalNamespace} from '../../../../helpers/models/namespaceHelpers';
import {TeamModel} from '../../../../models/api/teamModel';
import {TeamAvatar} from '../../avatars/teamAvatar';
import {NamespaceDropdown} from './namespaceDropdown';

/*
 * Props.
 */

interface NamespaceDropdownBoxProps {
  selectedNamespace: string | undefined;
  /** If undefined, the user namespace will not be a visible choice. */
  userNamespace: string | undefined;
  teams: ReadonlyArray<TeamModel>;
  onNamespaceSelect: HandlerOf<string>;
  isDisabled?: boolean;
  placeholder?: ReactNode;
  isRequired?: boolean;
  isDisplayOnly?: boolean;
  messageForDisplayOnly?: ReactNode;
  shouldRenderCompanyDropdownItem?: boolean;
  shouldRenderSharedDropdownItem?: boolean;
  shouldRenderVisibilityDescription?: boolean;
  shouldPrioritizeUser?: boolean;
  header?: React.ReactNode;
  label?: React.ReactNode;
  maxHeight?: number;
  shouldSkipTeamCheck?: boolean;
  dropdownWidth?: number;
}

/*
 * Styles.
 */

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
  margin-right: 8px;
`;

const StyledNameDiv = styled.div`
  ${ellipsis()};
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  ${ellipsis()};
`;

/*
 * Component.
 */

export const NamespaceDropdownBox: FC<NamespaceDropdownBoxProps> = (props) => (
  <DropdownBoxCoordinator
    label={props.label || <Trans id="dropdown-namespace-label">Access</Trans>}
    isDisabled={computeIsDisabled(props)}
    value={renderValue(props)}
    renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
    renderTooltip={() => renderTooltip(props)}
    isRequired={props.isRequired}
  />
);

/*
 * Helpers.
 */

function renderValue(props: NamespaceDropdownBoxProps) {
  const {
    selectedNamespace,
    teams,
    placeholder,
    shouldRenderSharedDropdownItem,
    shouldRenderCompanyDropdownItem,
  } = props;
  const team = teams.find((t) => t.namespace === selectedNamespace);

  // If no selectedNamespace, render the placeholder, if one exists.
  if (!selectedNamespace && placeholder) {
    return (
      <StyledValueDiv>
        <StyledPlaceholderDiv>{placeholder}</StyledPlaceholderDiv>
      </StyledValueDiv>
    );
  }

  const isGlobalSelected = selectedNamespace === globalNamespace;

  if (isGlobalSelected && shouldRenderSharedDropdownItem) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledValueDiv>
            <StyledIconDiv>
              <Icon name="sharedFilledSmall" color={theme.greys.shade70} />
            </StyledIconDiv>
            <StyledNameDiv>
              <Trans id="dropdown-namespace-shared">Shared</Trans>
            </StyledNameDiv>
          </StyledValueDiv>
        )}
      </UserThemeConsumer>
    );
  }

  if (isGlobalSelected && shouldRenderCompanyDropdownItem) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledValueDiv>
            <StyledIconDiv>
              <Icon name="buildingFilledSmall" color={theme.greys.shade70} />
            </StyledIconDiv>
            <StyledNameDiv>
              <Trans id="dropdown-namespace-company">Company</Trans>
            </StyledNameDiv>
          </StyledValueDiv>
        )}
      </UserThemeConsumer>
    );
  }

  // If no team was found, this is the private namespace.
  if (!team) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledValueDiv>
            <StyledIconDiv>
              <Icon name="lockSmall" color={theme.greys.shade60} />
            </StyledIconDiv>
            <StyledNameDiv>
              <Trans id="dropdown-namespace-teammate">Private</Trans>
            </StyledNameDiv>
          </StyledValueDiv>
        )}
      </UserThemeConsumer>
    );
  }

  // Otherwise, render the selected team.
  return (
    <StyledValueDiv>
      <StyledIconDiv>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
      </StyledIconDiv>
      <StyledNameDiv>{team.name}</StyledNameDiv>
    </StyledValueDiv>
  );
}

function renderDropdown(popoverProps: PopoverTransferProps, props: NamespaceDropdownBoxProps) {
  return <NamespaceDropdown {...popoverProps} {...props} width={props.dropdownWidth} />;
}

function renderTooltip(props: NamespaceDropdownBoxProps) {
  if (!props.isDisplayOnly) {
    return undefined;
  }

  if (props.messageForDisplayOnly) {
    return props.messageForDisplayOnly;
  }

  return <Trans id="namespace-dropdown-noedit">Cannot edit access after tag is created</Trans>;
}

function computeIsDisabled(props: NamespaceDropdownBoxProps) {
  const {teams, isDisplayOnly, shouldSkipTeamCheck, isDisabled} = props;

  if (isDisabled) {
    return true;
  }
  // If this dropdown is display only, it should be disabled.
  if (isDisplayOnly) {
    return true;
  }
  // If the user doesn't have any teams and we should not skip this check, disable the dropdown.
  if (teams.length === 0 && !shouldSkipTeamCheck) {
    return true;
  }
  return false;
}
